import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';
import { createStore } from './create-store';

const STORAGE_KEY = 'eddy.dismissed-skill-runs';
const MAX_IDS = 200;

function loadIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function persistIds(ids: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* quota or storage disabled — dismissal becomes session-only */
  }
}

export interface DismissedSkillRunsStore {
  ids: Set<string>;
  dismiss: (id: string) => void;
  clear: () => void;
}

function createDismissedSkillRunsStore(): StoreApi<DismissedSkillRunsStore> {
  return createStore<DismissedSkillRunsStore>(
    'dismissed-skill-runs-store',
    (set, get) => ({
      ids: new Set(loadIds()),
      dismiss: (id) => {
        if (get().ids.has(id)) return;
        const arr = [...get().ids, id];
        // FIFO trim so the localStorage entry can't grow without bound after
        // months of use. Old entries that age out of the server's recent
        // ring buffer no longer need to be filtered anyway.
        const trimmed = arr.length > MAX_IDS ? arr.slice(arr.length - MAX_IDS) : arr;
        persistIds(trimmed);
        set({ ids: new Set(trimmed) });
      },
      clear: () => {
        persistIds([]);
        set({ ids: new Set() });
      },
    }),
  );
}

let store: StoreApi<DismissedSkillRunsStore> | undefined;

function getStore(): StoreApi<DismissedSkillRunsStore> {
  if (!store) {
    store = createDismissedSkillRunsStore();
    // Cross-tab sync: when another tab writes the same key, re-parse and
    // update this tab's set so a dismiss in tab A makes the card disappear
    // in tab B within ~2 s (the next background-tasks poll re-renders).
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        if (e.key !== STORAGE_KEY || !store) return;
        try {
          const parsed = e.newValue ? (JSON.parse(e.newValue) as string[]) : [];
          store.setState({ ids: new Set(parsed) });
        } catch {
          /* malformed value — ignore */
        }
      });
    }
  }
  return store;
}

export function useDismissedSkillRuns<T>(selector: (s: DismissedSkillRunsStore) => T): T {
  return useStore(getStore(), selector);
}

/** Imperative clear (no React) — used by account-scope on user switch (FRONT-732). */
export function clearDismissedSkillRuns(): void {
  getStore().getState().clear();
}
